/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * User Administration: a person being created, deleted, edited, or having their password changed.
 *
 * The producer is bonito, which writes kebab-case keys and a flat record — `first-name`,
 * `privilege-options`, `default-units.distance`. Three of its shapes read badly as a generic diff
 * and are handled here:
 *
 *  1. A create or delete embeds the whole user in one diff entry, which the shared snapshot card
 *     renders as a single unreadable blob. This renderer opts into `handlesCreateDelete` and lays
 *     the user out as the administration screen does — details, units, profile, privileges.
 *  2. Privileges are an array, and goldeye diffs arrays by index. Removing one privilege therefore
 *     shifts every later index and arrives as a cascade of unrelated-looking replacements. The
 *     before and after sets are reconstructed instead, so the panel says what was actually granted
 *     and revoked.
 *  3. A password change carries no field edit at all — only the flag `password-updated: true`,
 *     which reads as "Password enabled". It is presented as the event it is.
 */

import React from 'react'

import { FiKey, FiUserCheck, FiUserMinus, FiUserPlus } from 'react-icons/fi'

import { asRecord } from './entityGroups'
import type { RendererProps } from './types'
import { useRoleNames } from './useAuditLookups'
import { useCompanyName } from './useCompanyName'

import type { AuditDetail } from '../../types'
import { humaniseSegment } from '../diff/fieldLabels'
import type { NormalizedChange } from '../diff/normalize'
import type { FieldRow } from '../primitives'
import { ChangeList, FieldTable, LookupLoading, Section } from '../primitives'

// ---- Field names -----------------------------------------------------------

const PRIVILEGES = 'privilege-options'
const STATUS_OPTIONS = 'status-screen-display-options'
const PASSWORD_UPDATED = 'password-updated'
const IS_REGISTERED = 'is-registered'
const UNITS = 'default-units'

/**
 * A user's profile, recorded as a stable id alongside the name it carried at the time.
 *
 * bonito only began writing the name in September 2026, so any older record has the id alone. Both
 * are presented together where available — the name is what a reader recognises, the id is what
 * survives the profile being renamed.
 */
const PROFILE_ID = 'user-profile'
const PROFILE_NAME = 'user-profile-name'

/**
 * How the administration screen orders the user's details, so the panel reads in the order the
 * person filling the form saw them.
 */
const DETAIL_FIELDS = ['first-name', 'last-name', 'email', 'phone-number', 'company', 'timezone-info-id'] as const

/**
 * Recorded, but never shown.
 *
 * The administration screen hides the default application until Fresh integration lands, so a value
 * nobody can set is not worth a row here either. It stays in the audit record and in the raw JSON
 * escape hatch — this only keeps it out of the layout, and out of "Other values" with it.
 */
const HIDDEN_FIELDS = ['default-application-type'] as const

/** Everything this renderer accounts for. Anything else is genuinely unexpected. */
const PRESENTED = new Set<string>([
  ...DETAIL_FIELDS,
  ...HIDDEN_FIELDS,
  UNITS,
  PROFILE_ID,
  PROFILE_NAME,
  PRIVILEGES,
  STATUS_OPTIONS,
  'is-sso-user',
])

const FIELD_LABELS: Record<string, string> = {
  'first-name': 'First name',
  'last-name': 'Last name',
  email: 'Email',
  'phone-number': 'Mobile',
  company: 'Company',
  'timezone-info-id': 'Timezone',
  'default-application-type': 'Default application',
  distance: 'Distance',
  temperature: 'Temperature',
}

/** bonito stores the default application as the legacy numeric enum. */
const APPLICATION_TYPES: Record<number, string> = {
  0: 'No default',
  1: 'Fresh InTransit',
  2: 'Fresh InStore',
  3: 'Fresh InSide',
}

/** The dialling codes the administration form offers, longest first so `+64` wins over `+6`. */
const COUNTRY_CODES = ['+61', '+64', '+1']

/**
 * Group a mobile number the way the administration form displays it: `+61755523994` reads as
 * `+61 755 523 994`.
 *
 * This deliberately reproduces bonito's own `formatPhoneNumber` — threes from the left after the
 * dialling code — rather than applying real national conventions. The point is that the audit panel
 * and the screen the change was made on show the same string, so a number can be compared between
 * them at a glance. A value that is not a recognised code followed by digits is left exactly as
 * stored; guessing at a format we do not recognise would be worse than showing the raw number.
 */
export const formatPhoneNumber = (value: string): string => {
  const compact = value.replace(/\s+/g, '')
  const code = COUNTRY_CODES.find((candidate) => compact.startsWith(candidate))
  if (!code) return value

  const digits = compact.slice(code.length)
  if (!/^\d+$/.test(digits)) return value

  const groups = /^(\d{1,3})(\d{1,3})(\d+)?$/.exec(digits)
  if (!groups) return `${code} ${digits}`

  return `${code} ${groups.slice(1).filter(Boolean).join(' ')}`
}

// ---- Snapshot shape --------------------------------------------------------

type User = Record<string, unknown>

/**
 * The whole user, however the writer recorded it.
 *
 * bonito sends the complete record as `old_value` or `new_value`, which goldeye turns into a single
 * entry at the empty path. The per-field fallback mirrors the alert subscription renderer: a record
 * whose every field was removed at once is the entity itself, not a list of unrelated deletions.
 */
const snapshotOf = (changes: NormalizedChange[]): User | null => {
  const whole = changes.find((c) => c.segments.length === 0)
  if (whole) return asRecord(whole.after ?? whole.before)

  const fields = changes.filter((c) => c.segments.length === 1)
  if (fields.length === 0 || fields.length !== changes.length) return null
  if (!fields.every((c) => c.state === 'REMOVED')) return null

  const user: User = {}
  for (const change of fields) user[change.segments[0]] = change.before ?? change.after
  return user
}

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []

// ---- Privilege sets --------------------------------------------------------

interface SetDelta {
  before: string[]
  after: string[]
  added: string[]
  removed: string[]
}

/**
 * Reconstruct what an id array held before and after, from index-keyed diff entries.
 *
 * goldeye walks an array by index, so dropping the second of five privileges arrives as three
 * replacements and one removal — none of which names the privilege that was actually revoked. An id
 * that merely shifted position appears on both sides and cancels out here, leaving only the grants
 * and revocations a reader cares about.
 */
export const buildSetDelta = (changes: NormalizedChange[]): SetDelta => {
  const before = new Set<string>()
  const after = new Set<string>()

  for (const change of changes) {
    // The whole array replaced at once, rather than element by element.
    if (change.segments.length === 1) {
      asStringArray(change.before).forEach((id) => before.add(id))
      asStringArray(change.after).forEach((id) => after.add(id))
      continue
    }

    if (typeof change.before === 'string') before.add(change.before)
    if (typeof change.after === 'string') after.add(change.after)
  }

  return {
    before: [...before],
    after: [...after],
    added: [...after].filter((id) => !before.has(id)),
    removed: [...before].filter((id) => !after.has(id)),
  }
}

// ---- Privilege chips -------------------------------------------------------

type ChipTone = 'neutral' | 'added' | 'removed'

const CHIP_TONES: Record<ChipTone, string> = {
  neutral: 'border-base-300 bg-base-200',
  added: 'border-success/40 bg-success/10 text-success',
  removed: 'border-error/40 bg-error/10 text-error line-through',
}

/**
 * One privilege.
 *
 * The GUID is kept on the element's title rather than beside the name: unlike an asset id it is not
 * something anyone reads or quotes, and rendering it inline would double the width of every chip.
 * A privilege whose name has not resolved falls back to a short prefix, which is still enough to
 * tell two chips apart.
 */
const PrivilegeChip: React.FC<{ id: string; name?: string; tone?: ChipTone }> = ({ id, name, tone = 'neutral' }) => (
  <span
    title={id}
    className={`inline-flex items-center gap-1 rounded-selector border px-2 py-0.5 text-xs ${CHIP_TONES[tone]}`}
  >
    {tone !== 'neutral' && <span className="sr-only">{tone === 'added' ? 'Granted:' : 'Revoked:'}</span>}
    <span className="font-medium">{name ?? `Privilege ${id.slice(0, 8)}…`}</span>
  </span>
)

const ChipRow: React.FC<{
  label?: string
  ids: string[]
  tone: ChipTone
  names: Map<string, string>
}> = ({ label, ids, tone, names }) => {
  if (ids.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5">
      {label && <p className="text-[11px] font-semibold uppercase tracking-wide text-base-content/60">{label}</p>}
      <div className="flex flex-wrap gap-1.5">
        {ids.map((id) => (
          <PrivilegeChip key={id} id={id} name={names.get(id.toLowerCase())} tone={tone} />
        ))}
      </div>
    </div>
  )
}

// ---- Value rendering -------------------------------------------------------

/**
 * Field encodings that read the same wherever the value appears — a create card, a delete card, or
 * one side of an update row.
 *
 * An unset mobile becomes null rather than an empty string so it renders as the panel's absent-value
 * dash. "Empty" implies a value was deliberately blanked; for a field that is optional on the form,
 * "not set" and "set to nothing" are the same fact and should look the same.
 */
const decode = (field: string, value: unknown): unknown => {
  if (field === 'default-application-type' && typeof value === 'number') {
    return APPLICATION_TYPES[value] ?? `Type ${String(value)}`
  }

  if (field === 'phone-number' && typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? formatPhoneNumber(trimmed) : null
  }

  return value
}

// ---- User profile ----------------------------------------------------------

/**
 * A profile as one string: the name it had, with the id that identifies it regardless.
 *
 * Either half may be missing — a record written before bonito recorded names has no name, and a
 * user with no profile has neither — so every combination has to read sensibly on its own.
 */
const profileText = (name: unknown, id: unknown): string | null => {
  const named = typeof name === 'string' && name.trim() ? name.trim() : null
  const identified = id == null ? null : `#${String(id)}`

  if (named && identified) return `${named} (${identified})`
  return named ?? identified
}

/**
 * Why the panel shows both halves.
 *
 * The name is a snapshot: it is what the profile was called when the change was made, and profiles
 * can be renamed afterwards, so it may no longer match the profile's name today. The id cannot
 * change, which is what makes it the half worth quoting. Saying so is the difference between a
 * reader trusting the name and a reader being misled by it.
 */
const ProfileNote: React.FC = () => (
  <p className="mt-2 text-xs text-base-content/60">
    The name is the one this profile had when the change was made — profiles can be renamed since, so it may differ from
    the profile’s name today. The id never changes.
  </p>
)

// ---- Value rendering -------------------------------------------------------

type CompanyName = (raw: unknown) => string | undefined

/**
 * `decode`, plus the user's company by name, or its id when no name can be found.
 *
 * The name comes from the viewer's company list. The audit record's own company name — which the
 * list endpoint resolved server-side — stands in when the list does not hold it, since the user's
 * company is nearly always that one. Otherwise the bare id is shown.
 */
const valueFor = (field: string, value: unknown, audit: AuditDetail, companyName: CompanyName): unknown => {
  if (field === 'company' && value != null && value !== '') {
    const name =
      companyName(value) ?? (String(value) === String(audit.companyId) ? (audit.companyName ?? undefined) : undefined)
    return name ?? value
  }

  return decode(field, value)
}

const detailRows = (user: User, audit: AuditDetail, companyName: CompanyName): FieldRow[] =>
  DETAIL_FIELDS.filter((field) => field in user).map((field) => ({
    label: FIELD_LABELS[field] ?? field,
    value: valueFor(field, user[field], audit, companyName),
    title: field,
  }))

const unitRows = (user: User): FieldRow[] => {
  const units = asRecord(user[UNITS])
  if (!units) return []

  return Object.entries(units).map(([key, value]) => ({
    label: FIELD_LABELS[key] ?? key,
    value,
    title: `${UNITS}.${key}`,
  }))
}

/**
 * Keys the snapshot carries that this renderer does not lay out.
 *
 * Read off the snapshot object rather than off the diff: a create and a delete arrive as one entry
 * holding the whole user, so there are no per-field changes to filter — a field bonito adds later
 * would otherwise be dropped silently, which is the one thing the panel must never do.
 */
const extraRows = (user: User): FieldRow[] =>
  Object.entries(user)
    .filter(([key]) => !PRESENTED.has(key))
    .map(([key, value]) => ({ label: humaniseSegment(key), value, title: key }))

// ---- Headline --------------------------------------------------------------

/**
 * What happened, in one sentence, before any of the detail.
 *
 * Support opens a deletion record to answer "who was removed, and who removed them" — a question
 * the field table can only be made to answer by reading it. The actor also appears in the panel's
 * metadata rail, but that is a separate column describing the record; this is the sentence
 * describing the event.
 */
const Headline: React.FC<{
  audit: AuditDetail
  user: User
  icon: React.ReactNode
  tone: string
  verb: string
  note?: string
}> = ({ audit, user, icon, tone, verb, note }) => {
  const fullName = [user['first-name'], user['last-name']].filter((part) => typeof part === 'string' && part).join(' ')
  const actor = audit.userName ?? (audit.userId ? `User #${audit.userId}` : 'Someone')
  const email = typeof user.email === 'string' ? user.email : ''

  return (
    <div className={`flex items-start gap-3 rounded-box border p-3 ${tone}`}>
      <span aria-hidden className="mt-0.5 shrink-0 text-lg">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-base font-semibold text-base-content">
          {actor} {verb} <span className="font-semibold">{fullName || audit.entityName || 'a user'}</span>
        </p>
        <p className="mt-0.5 text-xs text-base-content/70">
          {audit.entityName && <span>{audit.entityName}</span>}
          {audit.entityName && email && <span aria-hidden> · </span>}
          {email && <span>{email}</span>}
        </p>
        {note && <p className="mt-1 text-xs text-base-content/70">{note}</p>}
      </div>
    </div>
  )
}

/**
 * A create records how the account was made, and the two routes end in different places.
 *
 * An SSO user's account is complete the moment it is created. A non-SSO user gets a temporary
 * account and a welcome email, and only becomes a usable account once they follow it and set a
 * password — so an account that appears inactive afterwards is the expected state for one route and
 * a fault for the other. That distinction is worth a sentence rather than a boolean row.
 *
 * Deliberately phrased without naming an application. The account belongs to the whole platform;
 * Fresh is one of the applications it can sign in to, and saying "added to Fresh" describes a
 * consequence as though it were the event.
 */
const creationNote = (user: User): string | undefined => {
  if (!(('is-sso-user' as string) in user)) return undefined

  return user['is-sso-user'] === true
    ? 'Created as an SSO user — the account was created successfully and is ready to use. No welcome email was sent.'
    : 'Created as a temporary account — a welcome email was sent, and the account becomes active once the user follows it and sets a password.'
}

// ---- Snapshot view (create / delete) ---------------------------------------

const SnapshotView: React.FC<{ audit: AuditDetail; user: User }> = ({ audit, user }) => {
  const deleted = audit.changeType === 'delete'
  const tone = deleted ? 'error' : 'success'
  const suffix = deleted ? ' — removed' : ''

  /**
   * A deletion shows who was removed and the privileges that account held.
   *
   * The question a deletion record is opened to answer is which person is gone and who removed
   * them. Measurement units and profile are settings of an account that no longer exists — they
   * cannot be acted on, and listing them buries the identity that can. Privileges remain useful
   * context because they explain the access the removed account had. The raw JSON still carries
   * every value for the rare case where the full configuration matters.
   */
  const configShown = !deleted

  const privileges = asStringArray(user[PRIVILEGES])
  const statusOptions = asStringArray(user[STATUS_OPTIONS])
  const profile = profileText(user[PROFILE_NAME], user[PROFILE_ID])
  // Only a record that actually carries a name needs the caveat about renaming.
  const profileNamed = typeof user[PROFILE_NAME] === 'string' && user[PROFILE_NAME].trim() !== ''

  // Privileges remain useful context on a deletion: they show the access the removed account had.
  const { data: roleNames, isLoading: rolesLoading } = useRoleNames(privileges)
  const names = roleNames ?? new Map<string, string>()

  const companyName = useCompanyName()
  const details = detailRows(user, audit, companyName)
  const units = unitRows(user)
  const extras = extraRows(user)

  if (rolesLoading) return <LookupLoading />

  return (
    <div className="flex flex-col gap-3">
      <Headline
        audit={audit}
        user={user}
        icon={deleted ? <FiUserMinus /> : <FiUserPlus />}
        tone={deleted ? 'border-error/40 bg-error/10 text-error' : 'border-success/40 bg-success/10 text-success'}
        verb={deleted ? 'deleted' : 'created'}
        note={deleted ? undefined : creationNote(user)}
      />

      {details.length > 0 && (
        <Section title={`User details${suffix}`} tone={tone}>
          <FieldTable rows={details} />
        </Section>
      )}

      {configShown && units.length > 0 && (
        <Section title={`Measurement units${suffix}`} tone={tone}>
          <FieldTable rows={units} />
        </Section>
      )}

      {/* Optional at creation time, so an absent profile is a legitimate state rather than a gap. */}
      {configShown && profile && (
        <Section title={`User profile${suffix}`} tone={tone}>
          <FieldTable rows={[{ label: 'Profile', value: profile, title: PROFILE_ID }]} />
          {profileNamed && <ProfileNote />}
        </Section>
      )}

      <Section title={`Privilege options${suffix}`} count={privileges.length} tone={tone}>
        {privileges.length > 0 ? (
          <ChipRow ids={privileges} tone="neutral" names={names} />
        ) : (
          <p className="text-sm text-base-content/60">No privileges.</p>
        )}
      </Section>

      {configShown && statusOptions.length > 0 && (
        <Section title={`Status screen options${suffix}`} count={statusOptions.length} defaultOpen={false} tone={tone}>
          <ChipRow ids={statusOptions} tone="neutral" names={new Map()} />
        </Section>
      )}

      {/* A field bonito adds after this build ships must appear rather than vanish silently. */}
      {configShown && extras.length > 0 && (
        <Section title="Other values" count={extras.length} defaultOpen={false} tone={tone}>
          <FieldTable rows={extras} />
        </Section>
      )}
    </div>
  )
}

// ---- Password and registration events --------------------------------------

/**
 * The two changes that are events rather than field edits.
 *
 * An administrator resetting a password writes only `password-updated: true`. A person completing
 * their own registration writes that alongside `is-registered` flipping to true — the same flag,
 * but a different event, and the actor is the person themselves rather than an administrator.
 */
const EventCard: React.FC<{ audit: AuditDetail; registered: boolean }> = ({ audit, registered }) => {
  const actor = audit.userName ?? (audit.userId ? `User #${audit.userId}` : 'Someone')
  const who = audit.entityName || 'this user'

  return (
    <div className="flex items-start gap-3 rounded-box border border-warning/40 bg-warning/10 p-3">
      <span aria-hidden className="mt-0.5 shrink-0 text-lg text-warning">
        {registered ? <FiUserCheck /> : <FiKey />}
      </span>
      <div className="min-w-0">
        <p className="text-base font-semibold text-base-content">
          {registered ? `${who} completed registration` : `${actor} changed the password for ${who}`}
        </p>
        <p className="mt-0.5 text-xs text-base-content/70">
          {registered
            ? 'The account was registered from the welcome email and a password was set. It is now active.'
            : 'The password itself is never recorded — only that it was changed.'}
        </p>
      </div>
    </div>
  )
}

// ---- Field-level update ----------------------------------------------------

/** Both sides of a change decoded, so an edited field reads as it does on the create card. */
const withDecodedValues =
  (audit: AuditDetail, companyName: CompanyName) =>
  (change: NormalizedChange): NormalizedChange => {
    const field = change.segments[0]

    return {
      ...change,
      before: valueFor(field, change.before, audit, companyName),
      after: valueFor(field, change.after, audit, companyName),
    }
  }

/**
 * Moving a user to a different profile writes the id and the name as two entries, which read as two
 * unrelated edits. They are one, so they are shown as one row naming both halves.
 *
 * Kept keyed on the id change, so a record written before names existed is unaffected, and a name
 * change arriving on its own — the profile was renamed, not reassigned — stays its own row.
 */
const combineProfileChanges = (changes: NormalizedChange[]): NormalizedChange[] => {
  const id = changes.find((c) => c.segments[0] === PROFILE_ID)
  const name = changes.find((c) => c.segments[0] === PROFILE_NAME)
  if (!id || !name) return changes

  const combined: NormalizedChange = {
    ...id,
    label: 'User profile',
    before: profileText(name.before, id.before),
    after: profileText(name.after, id.after),
  }

  return changes.filter((c) => c !== name).map((c) => (c === id ? combined : c))
}

const UpdateView: React.FC<{ audit: AuditDetail; changes: NormalizedChange[] }> = ({ audit, changes }) => {
  const companyName = useCompanyName()
  const privilegeChanges = changes.filter((c) => c.segments[0] === PRIVILEGES)
  const statusChanges = changes.filter((c) => c.segments[0] === STATUS_OPTIONS)
  const unitChanges = changes.filter((c) => c.segments[0] === UNITS)
  const passwordChange = changes.find((c) => c.segments[0] === PASSWORD_UPDATED)
  const registrationChange = changes.find((c) => c.segments[0] === IS_REGISTERED)

  const claimed = new Set<NormalizedChange>([...privilegeChanges, ...statusChanges, ...unitChanges])
  if (passwordChange) claimed.add(passwordChange)
  // Only fold the registration flag away when it is part of the registration event; on its own it
  // is an ordinary field change and belongs in the list.
  if (registrationChange && passwordChange) claimed.add(registrationChange)

  // The remaining fields go through the shared change row, which formats by type and so cannot know
  // that a string is a phone number or that a number is an application type. Decoding the two sides
  // here keeps an edited field reading the same as it does on the create and delete cards.
  const remaining = combineProfileChanges(
    changes.filter((c) => !claimed.has(c)).map(withDecodedValues(audit, companyName))
  )
  const profileTouched = remaining.some((c) => c.segments[0] === PROFILE_ID || c.segments[0] === PROFILE_NAME)

  const privileges = buildSetDelta(privilegeChanges)
  const statuses = buildSetDelta(statusChanges)

  const { data: roleNames, isLoading: rolesLoading } = useRoleNames([...privileges.added, ...privileges.removed])
  const names = roleNames ?? new Map<string, string>()

  if (rolesLoading) return <LookupLoading />

  return (
    <div className="flex flex-col gap-3">
      {passwordChange && <EventCard audit={audit} registered={Boolean(registrationChange)} />}

      {privilegeChanges.length > 0 && (
        <Section title="Privilege options" count={privileges.added.length + privileges.removed.length}>
          {privileges.added.length === 0 && privileges.removed.length === 0 ? (
            // Every id appeared on both sides: the array was reordered, not re-granted.
            <p className="text-sm text-base-content/60">The privileges were reordered; none was granted or revoked.</p>
          ) : (
            <div className="flex flex-col gap-3">
              <ChipRow label="Granted" ids={privileges.added} tone="added" names={names} />
              <ChipRow label="Revoked" ids={privileges.removed} tone="removed" names={names} />
            </div>
          )}
        </Section>
      )}

      {unitChanges.length > 0 && (
        <Section title="Measurement units" count={unitChanges.length}>
          <ChangeList changes={unitChanges} />
        </Section>
      )}

      {statusChanges.length > 0 && (
        <Section
          title="Status screen options"
          count={statuses.added.length + statuses.removed.length}
          defaultOpen={false}
        >
          <div className="flex flex-col gap-3">
            <ChipRow label="Added" ids={statuses.added} tone="added" names={new Map()} />
            <ChipRow label="Removed" ids={statuses.removed} tone="removed" names={new Map()} />
          </div>
        </Section>
      )}

      {remaining.length > 0 && (
        <Section title="User details" count={remaining.length}>
          <ChangeList changes={remaining} />
          {profileTouched && <ProfileNote />}
        </Section>
      )}
    </div>
  )
}

// ---- Entry point -----------------------------------------------------------

export const UserAdminRenderer: React.FC<RendererProps> = ({ audit, changes }) => {
  const isSnapshot = audit.changeType === 'create' || audit.changeType === 'delete'
  const user = isSnapshot ? snapshotOf(changes) : null

  // A create or delete whose payload is not a record at all falls through to the field view, which
  // ends at the no-changes panel rather than at an empty one.
  if (!user) return <UpdateView audit={audit} changes={changes} />

  return <SnapshotView audit={audit} user={user} />
}
